import { useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, FilePdf } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DatePicker } from "../components/DatePicker";
import { CompanySwitcher } from "../components/CompanySwitcher";
import { api, errMsg } from "../lib/api";
import { useT } from "../i18n";

const CATEGORIES = ["Travel", "Meals", "Lodging", "Transport", "Office Supplies", "Software", "Equipment", "Entertainment", "Utilities", "Other"];
const STATUSES = ["draft", "submitted", "approved", "rejected", "reimbursed"];
const FORMATS = ["generic", "quickbooks", "xero"];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5">{label}</label>
    {children}
  </div>
);

export default function ExportPage() {
  const { t } = useT();
  const [f, setF] = useState({ format: "generic", status: "all", category: "all", date_from: "", date_to: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const buildParams = (includeFormat) => {
    const params = includeFormat ? { format: f.format } : {};
    if (f.status !== "all") params.status = f.status;
    if (f.category !== "all") params.category = f.category;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    return params;
  };

  const download = async () => {
    setBusy(true);
    try {
      const resp = await api.get("/export/csv", { params: buildParams(true), responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `expenses_${f.format}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("toast_csv"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const resp = await api.get("/export/pdf", { params: buildParams(false), responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "expense_report.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("toast_pdf"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-2xl">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">{t("export_title")}</h1>
        <p className="text-sm text-slate-500">{t("export_sub")}</p>
      </div>

      <CompanySwitcher />

      <div className="border border-slate-200 p-5 sm:p-6 space-y-5">
        <Field label={t("format")}>
          <Select value={f.format} onValueChange={(v) => set("format", v)}>
            <SelectTrigger data-testid="export-format-select" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>{FORMATS.map((x) => <SelectItem key={x} value={x}>{t(`fmt_${x}`)}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("th_status")}>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="export-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all")}</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{t(`st_${s}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("th_category")}>
            <Select value={f.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="export-category-select" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all")}</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`cat_${c}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("from")}>
            <DatePicker testId="export-date-from" value={f.date_from} onChange={(v) => set("date_from", v)} placeholder={t("startDate")} />
          </Field>
          <Field label={t("to")}>
            <DatePicker testId="export-date-to" value={f.date_to} onChange={(v) => set("date_to", v)} placeholder={t("endDate")} />
          </Field>
        </div>
        <Button data-testid="export-download-btn" onClick={download} disabled={busy} className="rounded-none bg-slate-900 hover:bg-slate-700 w-full h-11">
          <DownloadSimple size={16} className="mr-2" /> {busy ? t("preparing") : t("downloadCsv")}
        </Button>
        <Button data-testid="export-pdf-btn" onClick={downloadPdf} disabled={busy} variant="outline" className="rounded-none w-full h-11 border-slate-300">
          <FilePdf size={16} className="mr-2" /> {t("downloadPdfFull")}
        </Button>
      </div>
    </div>
  );
}

